import {
	createBox, clickable, scrollable, xrKeyboard,
	text, selectable, editable, interaction, loadFont, loadFontFamily, lcars, getDefaults,
} from 'imperial-lib';

let font;
// The LCARS face (Antonio, imperial-lcars examples/user1.html) for `$type: 'lcars'` view nodes
// and the HUD frame (hud-lcars.js); loaded once, before the first tree is built.
const LCARS_FONT = 'https://cdn.worldapi.org/imperial-text-base/google/antonio/font.json';
let lcarsFont = null; // the resolved font (read synchronously below)
let lcarsFontLoading = null;
// the in-flight load is shared: the HUD, a page tree and the early prewarm (client.js) may ask at the same time
export function ensureLcarsFont() {
	return lcarsFontLoading || (lcarsFontLoading = loadFontFamily(LCARS_FONT).then(f => (lcarsFont = f)));
}
const makeBox = createBox({ text, selectable, editable, clickable, scrollable });
const styleKeys = new Set([
	'width', 'height', 'backgroundColor', 'backgroundOpacity', 'borderWidths', 'borderRadii', 'borderColors',
	'padding', 'margin', 'gap', 'direction', 'justifyContent', 'alignItems', 'overflow',
	'fontSize', 'textColor', 'color', 'textAlign', 'vAlign', 'textWrap', 'lineHeight',
	'letterSpacing', 'textOpacity', 'flexGrow', 'flexShrink', 'flexBasis', 'maxWidth',
	'$viewport', 'x', 'y', 'z', 'scale', 'visible', 'layoutIgnore',
]);

function resolveText(str, data) {
	if (typeof str !== 'string') return str;
	return str.replace(/\{([^}]+)\}/g, (_, path) => {
		let value = data;
		for (const key of path.split('.')) value = value?.[key];
		return value ?? '';
	});
}

const fieldValue = b => (b._text?.opts?.text || '').trim();

function nodeType(def) {
	if (def.$form) return 'form';
	return def.$type || (def.editable ? 'input' : (def.onClick || def.href) ? 'button' : def.text != null ? 'text' : 'box');
}

function nodeKey(def, index) {
	return def.key ?? def.name ?? def.id ?? `${nodeType(def)}:${index}`;
}

function capabilitySignature(def) {
	return JSON.stringify({
		text: def.text != null || !!def.selectable || !!def.editable,
		selectable: !!def.selectable,
		editable: !!def.editable,
		clickable: !!(def.onClick || def.href),
		scrollable: !!def.scrollable,
	});
}

function shallowEqual(a, b) {
	if (a === b) return true;
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) if (!shallowEqual(a[i], b[i])) return false;
		return true;
	}
	return false;
}

function setArray(fn, box, next, prev) {
	if (!Array.isArray(next) || shallowEqual(next, prev)) return false;
	fn.call(box, ...next);
	return true;
}

function boxChildren(box) {
	return box.children.filter(child => child.isBox && !child.userData?.layoutIgnore);
}

// STYLE CASCADE (user 2026-10-10: "the styles can be cascaded like with css", "those are not
// supposed to be separate things"): a node inherits font, fontSize, text colour and the button/
// input look from its ancestors; the root values are imperial-box-ui's own defaults (the same
// `setDefaults`/`getDefaults` the hand-built HUD panels use), the font is the LCARS face. A node
// sets `theme: {...}` to override for its whole subtree (dialog() does that for its blue buttons).
const THEME_KEYS = ['font', 'fontSize', 'textColor', 'buttonBg', 'buttonPadding', 'buttonRadii', 'inputBg', 'inputBorder', 'inputRadii'];
function baseTheme() {
	const d = getDefaults();
	return {
		font: lcarsFont || d.font || font, fontSize: d.fontSize, textColor: d.textColor,
		buttonBg: d.buttonBg, buttonPadding: [8, 14, 8, 14], buttonRadii: [8, 8, 8, 8],
		inputBg: d.inputBg, inputBorder: d.inputBorder, inputRadii: [4, 4, 4, 4],
	};
}
function childTheme(def, theme) {
	if (!def.theme) return theme;
	const next = { ...theme };
	for (const k of THEME_KEYS) if (def.theme[k] !== undefined) next[k] = def.theme[k];
	return next;
}
function applyTheme(props, def, theme) {
	delete props.theme;
	const type = nodeType(def);
	const hasText = def.text != null || def.editable || def.selectable;
	if (hasText || type === 'input') {
		if (props.font == null) props.font = theme.font;
		if (props.fontSize == null) props.fontSize = theme.fontSize;
		if (props.textColor == null && props.color == null) props.textColor = theme.textColor;
	}
	if (type === 'button') {
		if (props.backgroundColor == null) props.backgroundColor = theme.buttonBg;
		if (props.padding == null) props.padding = theme.buttonPadding;
		if (props.borderRadii == null) props.borderRadii = theme.buttonRadii;
	}
	if (type === 'input') {
		if (props.backgroundColor == null) props.backgroundColor = theme.inputBg;
		if (props.borderRadii == null) props.borderRadii = theme.inputRadii;
		if (props.borderWidths == null) props.borderWidths = [1, 1, 1, 1];
		if (props.borderColors == null) props.borderColors = [theme.inputBorder, theme.inputBorder, theme.inputBorder, theme.inputBorder];
	}
}

function prepareProps(def, ctx) {
	const props = { ...def };
	applyTheme(props, def, ctx.theme);
	if (props.$viewport) {
		// globalThis.__guiViewport: VR lays the HUD out at a smaller virtual viewport
		// (miserit-anthar.js relayoutGuiFor) so text/buttons come out bigger on the panel
		const vp = globalThis.__guiViewport;
		props.width = (vp && vp.width) || window.innerWidth || props.width || 900;
		props.height = (vp && vp.height) || window.innerHeight || props.height || 600;
	}
	if (props.text != null) props.text = resolveText(props.text, ctx.controller);
	delete props.children;
	delete props.name;
	delete props.key;
	delete props.id;
	delete props.$type;
	delete props.$viewport;
	delete props.x;
	delete props.y;
	delete props.z;
	delete props.scale;
	delete props.visible;
	delete props.layoutIgnore;

	const clickName = def.onClick;
	// `args: [...]` in a view node = serialisable arguments for the named handler
	// (list rows: onClick: 'select', args: [i]; settings: onClick: 'step', args: [key, ±1])
	const clickArgs = Array.isArray(def.args) ? def.args : [];
	delete props.onClick;
	delete props.args;
	delete props.href;
	// `href` = a link, like an anchor: the framework navigates, no handler in the page
	// (user 2026-10-11: "we have no href?")
	if (def.href) {
		props.clickable = true;
		props.onClick = () => navigation.loadPage(def.href);
	} else if (clickName && ctx.controller[clickName]) {
		props.clickable = true;
		props.onClick = () => {
			const result = clickName === 'submit' && ctx.formSubmit
				? ctx.formSubmit(ctx.collectForm())
				: ctx.controller[clickName].call(ctx.controller, ...clickArgs);
			Promise.resolve(result).catch(error => console.error(`UI click handler failed: ${clickName}`, error));
		};
	}
	return props;
}

function applyRuntimeProps(box, def) {
	if (def.$viewport) {
		const vp = globalThis.__guiViewport;
		box.layoutProps.width = (vp && vp.width) || window.innerWidth || box.layoutProps.width;
		box.layoutProps.height = (vp && vp.height) || window.innerHeight || box.layoutProps.height;
		box.setSize(box.layoutProps.width, box.layoutProps.height);
	}
	if (def.layoutIgnore != null) box.userData.layoutIgnore = !!def.layoutIgnore;
	if (def.visible != null) box.visible = !!def.visible;
	if (def.x != null || def.y != null || def.z != null) box.position.set(def.x ?? box.position.x, def.y ?? box.position.y, def.z ?? box.position.z);
	if (def.scale != null) box.scale.setScalar(def.scale);
}

function applyBoxProps(box, next, prev = {}) {
	let needsLayout = false;

	if ((next.width !== prev.width || next.height !== prev.height)
		&& (next.width != null || next.height != null)) {
		box.layoutProps.width = next.width ?? box.layoutProps.width;
		box.layoutProps.height = next.height ?? box.layoutProps.height;
		box.setSize(box.layoutProps.width || 0, box.layoutProps.height || 0);
		needsLayout = true;
	}
	if (Array.isArray(next.backgroundColor) && !shallowEqual(next.backgroundColor, prev.backgroundColor)) {
		box.setBackgroundColor(...next.backgroundColor);
	}
	if (next.backgroundOpacity != null && next.backgroundOpacity !== prev.backgroundOpacity) box.setBackgroundOpacity(next.backgroundOpacity);
	needsLayout = setArray(box.setBorderWidths, box, next.borderWidths, prev.borderWidths) || needsLayout;
	needsLayout = setArray(box.setBorderRadii, box, next.borderRadii, prev.borderRadii) || needsLayout;
	if (Array.isArray(next.borderColors) && !shallowEqual(next.borderColors, prev.borderColors)) box.setBorderColors(...next.borderColors);
	needsLayout = setArray(box.setPadding, box, next.padding, prev.padding) || needsLayout;
	if (Array.isArray(next.margin) && !shallowEqual(next.margin, prev.margin)) box.setMargin(...next.margin);
	if (next.gap !== prev.gap && next.gap != null) { box.setGap(next.gap); needsLayout = false; }
	if (next.direction !== prev.direction && next.direction != null) { box.setDirection(next.direction); needsLayout = false; }
	if (next.justifyContent !== prev.justifyContent && next.justifyContent != null) { box.setJustifyContent(next.justifyContent); needsLayout = false; }
	if (next.alignItems !== prev.alignItems && next.alignItems != null) { box.setAlignItems(next.alignItems); needsLayout = false; }
	if (next.overflow !== prev.overflow && next.overflow != null) { box.setOverflow(next.overflow); needsLayout = false; }

	for (const key of ['flexGrow', 'flexShrink', 'flexBasis']) {
		if (next[key] !== prev[key]) {
			if (next[key] == null) delete box.layoutProps[key];
			else box.layoutProps[key] = next[key];
			needsLayout = true;
		}
	}

	if (next.text !== prev.text && box._textHandle?.setContent) box._textHandle.setContent(next.text ?? '');
	if (needsLayout) box.layout();
}

function buildCtx(controller) {
	const ctx = {
		controller,
		theme: baseTheme(), // the style cascade's root (see applyTheme)
		inputs: [],
		formSubmit: null,
		collectForm() {
			const data = {};
			for (const inp of ctx.inputs) data[inp._fieldName] = fieldValue(inp);
			return data;
		},
	};
	return ctx;
}

// `$type: 'lcars'` — an imperial-lcars panel (frame with border labels/buttons/inputs) inside a
// view description, so routed pages keep the LCARS look (user 2026-10-10: "why did you leave the
// lcars layout? that's a box now"). Border items use handler NAMES like boxes: { button: 'X',
// onClick: 'close', args: [...] }; label/button texts resolve {placeholders}. The LcarsBox sits in a
// transparent wrapper Box (flex only places Box children); its children go into its content Box.
const LCARS_OPTS = ['width', 'height', 'fontSize', 'color', 'backgroundColor', 'gap', 'minArm', 'content'];
function lcarsSignature(def) {
	const o = {}; for (const k of LCARS_OPTS) if (def[k] !== undefined) o[k] = def[k];
	return JSON.stringify({ o, borders: def.borders || null });
}
function resolveBorders(borders, ctx) {
	if (!borders) return undefined;
	const out = {};
	for (const [side, cols] of Object.entries(borders)) {
		out[side] = {};
		for (const [col, items] of Object.entries(cols || {})) {
			out[side][col] = (items || []).map((it) => {
				const item = { ...it };
				if (typeof item.label === 'string') item.label = resolveText(item.label, ctx.controller);
				if (typeof item.button === 'string') item.button = resolveText(item.button, ctx.controller);
				const name = item.onClick, args = Array.isArray(item.args) ? item.args : [];
				delete item.args;
				if (item.href) {
					const href = item.href;
					delete item.href;
					item.onClick = () => navigation.loadPage(href);
				} else if (typeof name === 'string') {
					item.onClick = ctx.controller[name]
						? () => Promise.resolve(ctx.controller[name].call(ctx.controller, ...args)).catch(e => console.error(`UI click handler failed: ${name}`, e))
						: () => console.warn('[box-patcher] lcars border handler missing:', name);
				}
				return item;
			});
		}
	}
	return out;
}
// frame changed (borders, size, colours): LcarsBox#update keeps the root, the frame mesh and every unchanged text /
// click proxy and registers only what is new (imperial-lcars STATUS 2026-10-21); the content Box follows the new
// content area, its children are patched by the caller. Options not in the def fall back to the lib defaults.
function updateLcarsFrame(box, def, ctx) {
	const l = box.__lcars;
	const opts = {};
	for (const k of LCARS_OPTS) if (k !== 'content') opts[k] = def[k];
	const rb = resolveBorders(def.borders, ctx) || {};
	opts.borders = {};
	for (const side of ['top', 'right', 'bottom', 'left']) opts.borders[side] = rb[side] ?? null;
	l.update(opts);
	const cb = l.contentBox, ca = l.contentArea;
	applyBoxProps(cb, { ...(def.content || {}), width: ca.w, height: ca.h }, { ...(box.__lcarsContent || {}), width: cb.layoutProps.width, height: cb.layoutProps.height });
	box.__lcarsContent = def.content || {};
	box.__lcarsSig = lcarsSignature(def);
	box.layout();
}
function buildLcarsNode(def, ctx, index) {
	const children = [];
	const outerTheme = ctx.theme; ctx.theme = childTheme(def, ctx.theme);
	for (const [i, child] of (def.children || []).entries()) {
		const built = buildNode(child, ctx, i);
		if (Array.isArray(built)) children.push(...built); else children.push(built);
	}
	ctx.theme = outerTheme;
	const opts = { font: lcarsFont };
	for (const k of LCARS_OPTS) if (def[k] !== undefined) opts[k] = def[k];
	opts.borders = resolveBorders(def.borders, ctx);
	const l = lcars(opts, children);
	const wrapper = makeBox({ backgroundColor: [0, 0, 0, 0] });
	wrapper.add(l);
	wrapper.layout();
	wrapper.__uiType = 'lcars';
	wrapper.__uiKey = nodeKey(def, index);
	wrapper.__uiCaps = capabilitySignature(def);
	wrapper.__uiProps = {};
	wrapper.__lcars = l;
	wrapper.__lcarsSig = lcarsSignature(def);
	wrapper.__lcarsContent = def.content || {};
	const baseDispose = wrapper.dispose?.bind(wrapper);
	wrapper.dispose = () => { l.contentBox?.dispose?.(); l.dispose(); baseDispose?.(); };
	applyRuntimeProps(wrapper, def);
	return wrapper;
}

function buildNode(def, ctx, index = 0) {
	if (def.$type === 'lcars') return buildLcarsNode(def, ctx, index);
	if (def.$form) {
		if (def.onSubmit && ctx.controller[def.onSubmit]) ctx.formSubmit = ctx.controller[def.onSubmit].bind(ctx.controller);
		return (def.children || []).flatMap((child, i) => buildNode(child, ctx, i));
	}

	const children = [];
	const outerTheme = ctx.theme; ctx.theme = childTheme(def, ctx.theme);
	for (const [i, child] of (def.children || []).entries()) {
		const built = buildNode(child, ctx, i);
		if (Array.isArray(built)) children.push(...built);
		else children.push(built);
	}
	ctx.theme = outerTheme;

	const props = prepareProps(def, ctx);
	const box = makeBox(props, children);
	box.__uiType = nodeType(def);
	box.__uiKey = nodeKey(def, index);
	box.__uiCaps = capabilitySignature(def);
	box.__uiProps = props;
	applyRuntimeProps(box, def);

	if (def.name) {
		box._fieldName = def.name;
		if (def.editable) {
			ctx.inputs.push(box);
			box.addEventListener?.('submit', () => { if (ctx.formSubmit) ctx.formSubmit(ctx.collectForm()); });
		}
	}
	return box;
}

function patchNode(box, def, ctx, index = 0) {
	if (!box || box.__uiType !== nodeType(def) || box.__uiKey !== nodeKey(def, index) || box.__uiCaps !== capabilitySignature(def)) {
		box?.dispose?.();
		return buildNode(def, ctx, index);
	}

	// the text's wrap width is fixed when the text is created and the lib offers no setter: a text node whose
	// wrapping or wrap width changed (a page rendered first at the default size, then re-fitted to a phone
	// panel, #59) is rebuilt, otherwise it keeps wrapping at the old width and runs out of its box
	if (def.$type !== 'lcars' && def.text != null) {
		const prev = box.__uiProps || {};
		if ((def.maxWidth ?? null) !== (prev.maxWidth ?? null) || !!def.textWrap !== !!prev.textWrap) {
			box.dispose?.();
			return buildNode(def, ctx, index);
		}
	}

	if (def.$type === 'lcars') {
		// frame unchanged → patch only the content children (keeps scroll positions); else rebuild
		if (!box.__lcars?.contentBox || typeof box.__lcars.update !== 'function') { box.dispose?.(); return buildLcarsNode(def, ctx, index); }
		if (box.__lcarsSig !== lcarsSignature(def)) updateLcarsFrame(box, def, ctx); // borders/size changed: LcarsBox#update re-lays out in place, no rebuild
		const outerTheme = ctx.theme; ctx.theme = childTheme(def, ctx.theme);
		patchChildren(box.__lcars.contentBox, def.children || [], ctx);
		ctx.theme = outerTheme;
		box.__lcars.contentBox.layout();
		applyRuntimeProps(box, def);
		return box;
	}

	const props = prepareProps(def, ctx);
	applyBoxProps(box, props, box.__uiProps || {});
	applyRuntimeProps(box, def);
	box.__uiProps = props;

	if (def.name) {
		box._fieldName = def.name;
		if (def.editable) ctx.inputs.push(box);
	}

	const outerTheme = ctx.theme; ctx.theme = childTheme(def, ctx.theme);
	patchChildren(box, def.children || [], ctx);
	ctx.theme = outerTheme;
	remeasureAutoMain(box);
	box.layout();
	return box;
}

// imperial-box-ui sizes a container from its children only while its main-axis size is 0 (box.js layout /
// measureIntrinsic); a patched container keeps the size of the previous render, so a row added to a queue
// column never grew the column, its parents, nor the scroll extent of the page (#54: the new queue row sat
// below the reachable area until the route was reloaded). A box whose main size the view did not set is
// set back to 0 here, so the layout() right after measures it again, bottom-up (children are patched first).
function remeasureAutoMain(box) {
	const lp = box.layoutProps;
	if (!lp || !boxChildren(box).length) return;
	const isRow = lp.direction === 'row';
	if (isRow ? lp._widthExplicit : lp._heightExplicit) return;
	if (isRow) box.setSize(0, lp.height);
	else box.setSize(lp.width, 0);
}

function patchChildren(parent, defs, ctx) {
	const oldChildren = boxChildren(parent);
	const oldByKey = new Map(oldChildren.map(child => [`${child.__uiType}:${child.__uiKey}`, child]));
	const nextChildren = [];

	for (const [i, def] of defs.entries()) {
		if (def.$form) {
			if (def.onSubmit && ctx.controller[def.onSubmit]) ctx.formSubmit = ctx.controller[def.onSubmit].bind(ctx.controller);
			for (const [j, child] of (def.children || []).entries()) {
				const key = `${nodeType(child)}:${nodeKey(child, j)}`;
				const patched = patchNode(oldByKey.get(key), child, ctx, j);
				oldByKey.delete(key);
				nextChildren.push(patched);
			}
			continue;
		}
		const key = `${nodeType(def)}:${nodeKey(def, i)}`;
		const patched = patchNode(oldByKey.get(key), def, ctx, i);
		oldByKey.delete(key);
		nextChildren.push(patched);
	}

	for (const child of oldByKey.values()) child.dispose?.();
	for (const child of oldChildren) if (child.parent === parent) parent.remove(child);
	for (const child of nextChildren) parent.add(child);
}

// Tab: cycles the fields of the current view while one of them is focused; otherwise it toggles
// the whole HUD (user 2026-10-10; client/hud-toggle.js installs that global handler)
function installTabHandler(controller, inputs) {
	if (controller._tabHandler) window.removeEventListener('keydown', controller._tabHandler);
	if (!inputs.length) return;
	controller._tabHandler = function tabHandler(e) {
		if (e.key !== 'Tab') return;
		if (!game.gui.children.length) { window.removeEventListener('keydown', tabHandler); return; }
		const cur = inputs.findIndex(b => b.selection?.active);
		if (cur < 0) return; // no field focused → the HUD toggle handles Tab
		e.preventDefault();
		const next = e.shiftKey ? (cur <= 0 ? inputs.length - 1 : cur - 1) : (cur + 1) % inputs.length;
		game._input?._focus(inputs[next]);
		inputs[next].selection?.setCursor((inputs[next]._text?.opts?.text || '').length);
	};
	window.addEventListener('keydown', controller._tabHandler);
}

export function ensureInteractionRuntime() {
	if (game._input) return;
	game._input = interaction({ renderer: game.renderer, scene: game.scene, camera: game.camera, controls: game.controls, manageXR: 'attach' });
	// The XR keyboard needs the dialogs' DejaVu face (757 kB, uncompressed): it attaches when the font has
	// arrived, so the HUD does not wait for it (desktop-load #58). keyboards.xr is null until then (interaction() reads it lazily).
	const input = game._input;
	ensureFont().then(kbFont => { input.keyboards.xr = xrKeyboard({
		font: kbFont,
		place(kb) {
			const H = window.innerHeight || 900;
			const t = window.__kbTune || {};
			kb.userData.layoutIgnore = true;
			// The keyboard lives in its own Group with a HIGH renderOrder: three sorts transparent
			// meshes by the nearest Group's renderOrder (groupOrder) BEFORE the mesh's own, and
			// the LCARS overlays are Groups at base+10 — so the key meshes' 20000+ never counted
			// and an open overlay painted over the keyboard (user 2026-10-11: "the keyboard cuts
			// into the panel"). A Box is a Mesh, so the keyboard root itself cannot carry it.
			if (!game._kbHolder) {
				game._kbHolder = new (game.gui.constructor)();
				game._kbHolder.renderOrder = (game.gui.renderOrder || 10000) + 10000;
				game._kbHolder.userData.layoutIgnore = true;
			}
			if (game._kbHolder.parent !== game.gui) game.gui.add(game._kbHolder);
			if (kb.parent !== game._kbHolder) game._kbHolder.add(kb);
			kb.scale.setScalar(t.scale ?? 1.25);
			// z 160 = clearly in front of the HUD plane: at z 0 the top key row tied with the
			// page rows behind it, the press went to the row, the field blurred and the
			// keyboard closed (user 2026-10-11: "cant select the first row with the numbers")
			kb.position.set(t.x ?? 0, t.y ?? -H * 0.55, t.z ?? 160);
			kb.rotation.set(t.tilt ?? -0.99, 0, 0);
			kb.updateRenderOrder?.((game.gui.renderOrder || 10000) + 10000);
		},
	}); });

	// No "lift the dialog while the keyboard is open" any more: the keyboard sits BELOW the
	// frame now (2026-10-11), and the lift's restore on blur moved the SEND/LOGIN button out
	// from under the ray between press and release, so the first press on it only closed the
	// keyboard and the second one clicked (user 2026-10-11: "clicking on the send button makes
	// the keyboard disappear you have to click on send again").
	let last = performance.now();
	game.onFrame(() => {
		const now = performance.now();
		game._input.update(now - last);
		last = now;
	});
}

let fontLoading = null;
export function ensureFont() {
	return fontLoading || (fontLoading = loadFont('/assets/DejaVuSans.ttf').then(f => (font = f)));
}

export async function buildBoxTree(view, controller) {
	await ensureLcarsFont(); // the theme face of every node; DejaVu (ensureFont) is only the fallback and loads on its own
	const ctx = buildCtx(controller);
	const built = buildNode(view, ctx, 0);
	const container = Array.isArray(built) ? makeBox({ direction: 'column' }, built) : built;
	container.__uiRootView = view;
	if (game.gui.renderOrder) container.updateRenderOrder(game.gui.renderOrder);
	installTabHandler(controller, ctx.inputs);
	controller._inputs = ctx.inputs;
	controller._container = container;
	ensureInteractionRuntime();
	return container;
}

export async function patchBoxTree(container, view, controller) {
	if (!container) return buildBoxTree(view, controller);
	await ensureLcarsFont();
	const ctx = buildCtx(controller);
	const next = patchNode(container, view, ctx, 0);
	if (next !== container && container.parent) container.parent.add(next);
	if (game.gui.renderOrder) next.updateRenderOrder(game.gui.renderOrder);
	installTabHandler(controller, ctx.inputs);
	controller._inputs = ctx.inputs;
	controller._container = next;
	ensureInteractionRuntime();
	return next;
}
